import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Activity, Shield, BookOpen, Printer, Mail, Clock, Sparkles, Zap, Droplet, Crown, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/shared";

export const EDU_ICONS = { BookOpen, Sparkles, Zap, Droplet, Shield, Activity, Clock, Heart };

export const CATEGORY_ORDER = ['IV Hydration', 'IV Drips', 'Injections', 'Before & After'];

export const CATEGORY_META = {
  'IV Hydration': { icon: Droplet, tint: 'from-cyan-500/15 to-cyan-600/5', accent: 'text-cyan-300', desc: 'Pure rehydration & electrolytes' },
  'IV Drips': { icon: Crown, tint: 'from-amber-500/15 to-amber-600/5', accent: 'text-primary', desc: 'Premium vitamin & wellness infusions' },
  'Injections': { icon: Zap, tint: 'from-rose-500/15 to-rose-600/5', accent: 'text-rose-300', desc: 'Targeted shots for fast results' },
  'Before & After': { icon: Shield, tint: 'from-emerald-500/15 to-emerald-600/5', accent: 'text-emerald-300', desc: 'Visit prep & aftercare guidance' },
};

export const EducationLibrary = ({ patients }) => {
  const [docs, setDocs] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/educational-documents`).then(r => setDocs(r.data)).catch(() => toast.error('Failed to load documents'));
  }, []);

  const openDoc = async (docId) => {
    try {
      const res = await axios.get(`${API}/educational-documents/${docId}`);
      setSelectedDoc(res.data);
    } catch { toast.error('Failed to load document'); }
  };

  const printDoc = () => window.print();

  const sendDoc = async () => {
    if (!selectedPatientId) { toast.error('Choose a patient'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/educational-documents/email`, { document_id: selectedDoc.id, patient_id: selectedPatientId });
      toast.success('Document emailed successfully');
      setEmailModalOpen(false);
      setSelectedPatientId('');
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const docsByCategory = useMemo(() => {
    const groups = {};
    docs.forEach(d => { (groups[d.category] = groups[d.category] || []).push(d); });
    return groups;
  }, [docs]);

  const orderedCategories = useMemo(() =>
    [...CATEGORY_ORDER.filter(c => docsByCategory[c]), ...Object.keys(docsByCategory).filter(c => !CATEGORY_ORDER.includes(c))],
    [docsByCategory]
  );

  const DocIcon = selectedDoc ? (EDU_ICONS[selectedDoc.icon] || BookOpen) : BookOpen;

  return (
    <div className="space-y-8">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/15 via-amber-500/8 to-transparent border border-primary/30 p-8">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-amber-500/5 blur-3xl pointer-events-none" />
        <div className="relative z-10">
          <p className="text-xs font-semibold text-primary uppercase tracking-[0.3em] mb-2">Patient Education</p>
          <h2 className="text-3xl font-bold brand-heading mb-2"><span className="gold-gradient-text">Knowledge</span> <span className="silver-gradient-text">is Wellness</span></h2>
          <p className="text-sm text-muted-foreground max-w-xl leading-relaxed">Every drip and injection on our menu is broken down into plain-English patient education. Share these with clients to help them understand exactly what's flowing into their body and why.</p>
        </div>
      </div>

      {orderedCategories.map(cat => {
        const meta = CATEGORY_META[cat] || { icon: BookOpen, tint: 'from-primary/10 to-transparent', accent: 'text-primary', desc: '' };
        const CategoryIcon = meta.icon;
        return (
          <section key={cat}>
            <div className="flex items-end justify-between mb-4 pb-3 border-b border-primary/20">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.tint} border border-primary/30 flex items-center justify-center`}>
                  <CategoryIcon className={`w-5 h-5 ${meta.accent}`} />
                </div>
                <div>
                  <h3 className="text-lg font-bold brand-heading text-foreground">{cat}</h3>
                  <p className="text-xs text-muted-foreground">{meta.desc}</p>
                </div>
              </div>
              <span className="text-xs text-muted-foreground">{docsByCategory[cat].length} document{docsByCategory[cat].length === 1 ? '' : 's'}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {docsByCategory[cat].map(d => {
                const DIcon = EDU_ICONS[d.icon] || BookOpen;
                return (
                  <button key={d.id} type="button" onClick={() => openDoc(d.id)} data-testid={`doc-${d.id}`}
                    className="group text-left rounded-xl border border-border/40 bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-sm hover:border-primary/60 hover:shadow-[0_0_20px_rgba(212,175,55,0.15)] transition-all duration-300 overflow-hidden">
                    <div className="h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50 group-hover:opacity-100 transition-opacity" />
                    <div className="p-5 space-y-3">
                      <div className="flex items-start justify-between">
                        <div className={`w-9 h-9 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center group-hover:bg-primary/20 transition-colors`}>
                          <DIcon className="w-4 h-4 text-primary" />
                        </div>
                        <BookOpen className="w-3 h-3 text-muted-foreground/40 group-hover:text-primary/60 transition-colors" />
                      </div>
                      <div>
                        <p className="font-bold text-foreground group-hover:text-primary transition-colors leading-tight">{d.title}</p>
                        {d.tagline && <p className="text-xs text-primary/80 italic mt-1">{d.tagline}</p>}
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{d.summary}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}

      {docs.length === 0 && <p className="text-center text-muted-foreground py-8">Loading library...</p>}

      {/* Document viewer */}
      <Dialog open={!!selectedDoc} onOpenChange={(o) => !o && setSelectedDoc(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto print-area">
          <DialogHeader className="no-print">
            <DialogTitle className="sr-only">{selectedDoc?.title}</DialogTitle>
            <DialogDescription className="sr-only">{selectedDoc?.category} · {selectedDoc?.summary}</DialogDescription>
          </DialogHeader>

          {selectedDoc && (
            <div className="space-y-6">
              {/* Print-only branded header */}
              <div className="text-center pb-4 border-b border-border/40 print-only-block">
                <h1 className="text-2xl font-bold gold-gradient-text brand-heading">EliteIV Lounge</h1>
                <p className="text-xs text-muted-foreground tracking-widest uppercase mt-1">Patient Education</p>
              </div>

              {/* Hero of doc */}
              <div className="text-center space-y-2 pb-2 border-b border-primary/20">
                <div className="flex justify-center">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-amber-500/10 border border-primary/40 flex items-center justify-center">
                    <DocIcon className="w-7 h-7 text-primary" />
                  </div>
                </div>
                <p className="text-[10px] font-semibold text-primary uppercase tracking-[0.3em]">{selectedDoc.category}</p>
                <h2 className="text-2xl sm:text-3xl font-bold brand-heading"><span className="gold-gradient-text">{selectedDoc.title}</span></h2>
                {selectedDoc.tagline && <p className="text-sm text-muted-foreground italic">{selectedDoc.tagline}</p>}
              </div>

              {/* Ingredients */}
              {selectedDoc.ingredients && selectedDoc.ingredients.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-primary uppercase tracking-[0.25em]">What's Inside</p>
                  <div className="space-y-2">
                    {selectedDoc.ingredients.map(([name, desc], i) => (
                      <div key={i} className="rounded-lg border border-primary/20 bg-primary/5 p-3 pl-4 relative">
                        <div className="absolute left-0 top-3 bottom-3 w-0.5 bg-gradient-to-b from-primary via-amber-400 to-primary/40 rounded-r" />
                        <p className="font-semibold text-primary text-sm">{name}</p>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Content sections */}
              <div className="space-y-4">
                {selectedDoc.content.map(([title, body], i) => (
                  <div key={i} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="w-1 h-4 bg-primary rounded-full" />
                      <h4 className="font-semibold text-primary text-sm">{title}</h4>
                    </div>
                    <p className="text-sm text-foreground/85 leading-relaxed pl-3">{body}</p>
                  </div>
                ))}
              </div>

              {/* Print-only footer */}
              <div className="border-t border-border/40 pt-3 mt-4 text-center text-xs text-muted-foreground print-only-block">
                EliteIV Lounge · Premium IV Therapy &amp; Wellness · Questions? Call your provider.
              </div>
            </div>
          )}

          <DialogFooter className="no-print">
            <Button variant="outline" onClick={() => setSelectedDoc(null)}>Close</Button>
            <Button variant="outline" onClick={printDoc} data-testid="print-doc-btn"><Printer className="w-4 h-4 mr-1" />Print</Button>
            <Button onClick={() => setEmailModalOpen(true)} data-testid="email-doc-btn" className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold"><Mail className="w-4 h-4 mr-1" />Email to Patient</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email to patient modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email "{selectedDoc?.title}"</DialogTitle>
            <DialogDescription>Choose a patient — they'll receive a branded copy of this document.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Patient</Label>
            <Select value={selectedPatientId} onValueChange={setSelectedPatientId}>
              <SelectTrigger><SelectValue placeholder="Choose patient..." /></SelectTrigger>
              <SelectContent>{patients.filter(p => p.email).map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.email})</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Only patients with email on file are listed.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailModalOpen(false)}>Cancel</Button>
            <Button onClick={sendDoc} disabled={sending} className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold">{sending ? 'Sending...' : 'Send'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== HIPAA DISCLOSURE ====================
